import React, { useState } from 'react';
import type { Socket } from 'socket.io-client';

type KitType = 'wood' | 'stone' | 'metal';
type ItemCategory = 'construction' | 'destruction' | 'sign';

interface AdminPanelToolsTabProps {
  apiBaseUrl: string;
  socket?: Socket | null;
  playerId?: string;
  currentTile?: { x: number; y: number };
  isBusy: boolean;
  onStateChange: (state: { isBusy: boolean; lastMessage: string | null }) => void;
  onToggleCollisionDebug?: (enabled: boolean) => void;
  onToggleHitboxDebug?: (enabled: boolean) => void;
}

export const AdminPanelToolsTab: React.FC<AdminPanelToolsTabProps> = ({
  apiBaseUrl,
  socket,
  playerId,
  currentTile,
  isBusy,
  onStateChange,
  onToggleCollisionDebug,
  onToggleHitboxDebug,
}) => {
  const currentTileLabel: string = currentTile
    ? `${currentTile.x}, ${currentTile.y}`
    : 'unknown';
  const [teleportX, setTeleportX] = useState<string>('0');
  const [teleportY, setTeleportY] = useState<string>('0');
  const [chunkX, setChunkX] = useState<string>('0');
  const [chunkY, setChunkY] = useState<string>('0');
  const [kitCategory, setKitCategory] = useState<ItemCategory>('construction');
  const [kitType, setKitType] = useState<KitType>('wood');
  const [kitQuantity, setKitQuantity] = useState<string>('1');
  const [collisionDebugEnabled, setCollisionDebugEnabled] = useState<boolean>(false);
  const [hitboxDebugEnabled, setHitboxDebugEnabled] = useState<boolean>(false);

  const callAdminEndpoint = async (
    endpoint: string,
    actionName: string,
    data?: Record<string, any>
  ): Promise<void> => {
    onStateChange({ isBusy: true, lastMessage: `${actionName}...` });
    try {
      const res = await fetch(`${apiBaseUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data || {}),
      });
      const text: string = await res.text();
      const responseData = (() => {
        try {
          return JSON.parse(text);
        } catch {
          return null;
        }
      })();
      if (!res.ok) {
        const message: string =
          typeof responseData?.error === 'string'
            ? responseData.error
            : `HTTP ${res.status}: ${text.slice(0, 200)}`;
        throw new Error(message);
      }
      onStateChange({
        isBusy: false,
        lastMessage: responseData?.message || `${actionName} completed`,
      });
    } catch (err) {
      onStateChange({
        isBusy: false,
        lastMessage: `Error: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  const teleportToTile = (playerId: string, tileX: number, tileY: number): void => {
    void callAdminEndpoint('/api/admin/dev/teleport-to-tile', 'Teleport', {
      playerId,
      tileX,
      tileY,
    });
  };

  const spawnConstructionKits = (): void => {
    if (!socket || !playerId) {
      onStateChange({ isBusy: false, lastMessage: 'Socket not connected' });
      return;
    }

    const qty = parseInt(kitQuantity, 10);
    if (isNaN(qty) || qty < 1) {
      onStateChange({ isBusy: false, lastMessage: 'Invalid quantity' });
      return;
    }

    onStateChange({ isBusy: true, lastMessage: 'Spawning items...' });

    let itemId: string;
    if (kitCategory === 'construction') {
      itemId = `construction_kit_${kitType}`;
    } else if (kitCategory === 'destruction') {
      itemId = 'destruction_kit';
    } else {
      itemId = `sign_${kitType}`;
    }

    // Set a timeout in case the callback never fires
    const timeoutId = setTimeout(() => {
      onStateChange({ isBusy: false, lastMessage: 'Spawn request timed out' });
    }, 5000);

    socket.emit('dev:spawn_construction_kits', {
      kitType: kitCategory === 'construction' ? kitType : undefined,
      itemId: kitCategory === 'sign' || kitCategory === 'destruction' ? itemId : undefined,
      quantity: qty,
      usePlayerPosition: true,
    }, (response: { success: boolean; message: string }) => {
      clearTimeout(timeoutId);
      onStateChange({
        isBusy: false,
        lastMessage: response.message,
      });
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Debug Overlays */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10 }}>🧭 Debug Overlays</div>
        <div style={{ fontSize: 10, color: '#a0aec0', marginBottom: 4 }}>
          Current Tile: {currentTileLabel}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: '#e2e8f0' }}>
          <input
            type="checkbox"
            checked={collisionDebugEnabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const enabled: boolean = e.target.checked;
              setCollisionDebugEnabled(enabled);
              onToggleCollisionDebug?.(enabled);
            }}
            disabled={isBusy || !onToggleCollisionDebug}
          />
          Collision Grid
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: '#e2e8f0' }}>
          <input
            type="checkbox"
            checked={hitboxDebugEnabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const enabled: boolean = e.target.checked;
              setHitboxDebugEnabled(enabled);
              onToggleHitboxDebug?.(enabled);
            }}
            disabled={isBusy || !onToggleHitboxDebug}
          />
          Hitbox Visuals
        </label>
      </div>

      {/* Construction Kit Spawner */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10 }}>🔨 Item Spawner</div>
        
        <div style={{ marginBottom: 4, display: 'flex', gap: 2 }}>
          <select
            value={kitCategory}
            onChange={(e) => setKitCategory(e.target.value as ItemCategory)}
            disabled={isBusy}
            style={{
              flex: 1,
              padding: '2px 4px',
              fontSize: 9,
              backgroundColor: '#2d3748',
              color: '#edf2f7',
              border: '1px solid #4a5568',
              borderRadius: 0,
              cursor: isBusy ? 'default' : 'pointer',
            }}
          >
            <option value="construction">🔨 Construction</option>
            <option value="destruction">💥 Destruction</option>
            <option value="sign">📝 Signs</option>
          </select>
          
          <select
            value={kitType}
            onChange={(e) => setKitType(e.target.value as KitType)}
            disabled={isBusy}
            style={{
              flex: 1,
              padding: '2px 4px',
              fontSize: 9,
              backgroundColor: '#2d3748',
              color: '#edf2f7',
              border: '1px solid #4a5568',
              borderRadius: 0,
              cursor: isBusy ? 'default' : 'pointer',
            }}
          >
            <option value="wood">🪵 Wood</option>
            <option value="stone">🪨 Stone</option>
            <option value="metal">⚙️ Metal</option>
          </select>

          <input
            type="number"
            value={kitQuantity}
            onChange={(e) => setKitQuantity(e.target.value)}
            disabled={isBusy}
            min="1"
            max="100"
            style={{
              width: 40,
              padding: '2px 4px',
              fontSize: 9,
              backgroundColor: '#2d3748',
              color: '#edf2f7',
              border: '1px solid #4a5568',
              borderRadius: 0,
            }}
          />
        </div>

        <button
          type="button"
          onClick={spawnConstructionKits}
          disabled={isBusy}
          style={{
            width: '100%',
            padding: '2px 6px',
            fontSize: 10,
            backgroundColor: '#2ecc71',
            color: 'white',
            border: 'none',
            borderRadius: 0,
            cursor: isBusy ? 'default' : 'pointer',
            fontWeight: 'bold',
          }}
        >
          {isBusy ? 'Spawning...' : (kitCategory === 'construction' ? '🔨 Spawn Kits' : kitCategory === 'destruction' ? '💥 Spawn Kits' : '📝 Spawn Signs')}
        </button>
      </div>

      {/* Teleport to nearest Trading Post */}
      <button
        type="button"
        disabled={isBusy}
        onClick={() => {
          if (!playerId || !socket) {
            onStateChange({ isBusy: false, lastMessage: 'Socket not connected' });
            return;
          }
          // Instant teleport - emit and immediately show success
          socket.emit('admin:teleportToTradingPost', { playerId });
          onStateChange({ isBusy: false, lastMessage: '✓ Teleported to Trading Post' });
        }}
        style={{
          padding: '2px 6px',
          fontSize: 10,
          backgroundColor: '#2d3748',
          color: '#edf2f7',
          border: '1px solid #4a5568',
          borderRadius: 0,
          cursor: isBusy ? 'default' : 'pointer',
          width: '100%',
        }}
      >
        To Nearest Trading Post
      </button>

      {/* Teleport to nearest Whirlpool */}
      <button
        type="button"
        disabled={isBusy}
        onClick={() => {
          if (!playerId || !socket) {
            onStateChange({ isBusy: false, lastMessage: 'Socket not connected' });
            return;
          }
          // Instant teleport - emit and immediately show success
          socket.emit('admin:teleportToNearestWhirlpool', { playerId });
          onStateChange({ isBusy: false, lastMessage: '✓ Teleported to Whirlpool' });
        }}
        style={{
          padding: '2px 6px',
          fontSize: 10,
          backgroundColor: '#553c9a',
          color: '#e9d8fd',
          border: '1px solid #6b46c1',
          borderRadius: 0,
          cursor: isBusy ? 'default' : 'pointer',
          width: '100%',
        }}
      >
        To Nearest Whirlpool
      </button>

      {/* Reset to Core World */}
      <button
        type="button"
        disabled={isBusy}
        onClick={() => {
          if (!playerId || !socket) {
            onStateChange({ isBusy: false, lastMessage: 'Socket not connected' });
            return;
          }
          if (!window.confirm('Reset character to core world?')) return;
          // Instant reset - emit and immediately show success
          socket.emit('admin:resetToCoreWorld', { playerId });
          onStateChange({ isBusy: false, lastMessage: '✓ Reset to Core World' });
        }}
        style={{
          padding: '2px 6px',
          fontSize: 10,
          backgroundColor: '#7c2d12',
          color: '#fed7aa',
          border: '1px solid #ea580c',
          borderRadius: 0,
          cursor: isBusy ? 'default' : 'pointer',
          width: '100%',
        }}
      >
        Reset to Core World
      </button>

      {/* Teleport controls */}
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div>Teleport (tile)</div>
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            type="number"
            value={teleportX}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTeleportX(e.target.value)}
            placeholder="X"
            style={{ width: 50, fontSize: 10 }}
          />
          <input
            type="number"
            value={teleportY}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTeleportY(e.target.value)}
            placeholder="Y"
            style={{ width: 50, fontSize: 10 }}
          />
          <button
            type="button"
            disabled={isBusy}
            onClick={() => {
              if (!currentTile) {
                onStateChange({ isBusy: false, lastMessage: 'Tile position not available' });
                return;
              }
              setTeleportX(String(currentTile.x));
              setTeleportY(String(currentTile.y));
              onStateChange({ isBusy: false, lastMessage: 'Teleport coords set to current tile' });
            }}
            style={{
              padding: '2px 6px',
              fontSize: 10,
              backgroundColor: '#1f2933',
              color: '#e2e8f0',
              border: '1px solid #4a5568',
              borderRadius: 0,
              cursor: isBusy ? 'default' : 'pointer',
            }}
          >
            Here
          </button>
          <button
            type="button"
            disabled={isBusy}
            onClick={() => {
              if (!playerId) {
                onStateChange({ isBusy: false, lastMessage: 'playerId not available' });
                return;
              }
              const tileX = Number.parseInt(teleportX, 10);
              const tileY = Number.parseInt(teleportY, 10);
              if (Number.isNaN(tileX) || Number.isNaN(tileY)) {
                onStateChange({ isBusy: false, lastMessage: 'Enter valid tile coordinates' });
                return;
              }
              teleportToTile(playerId, tileX, tileY);
            }}
            style={{
              padding: '2px 6px',
              fontSize: 10,
              backgroundColor: '#2d3748',
              color: '#edf2f7',
              border: '1px solid #4a5568',
              borderRadius: 0,
              cursor: isBusy ? 'default' : 'pointer',
            }}
          >
            Go
          </button>
        </div>
      </div>

      {/* Generate/Load Chunks */}
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div>Generate/Load Chunks</div>
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            type="number"
            value={chunkX}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setChunkX(e.target.value)}
            placeholder="Chunk X"
            style={{ width: 60, fontSize: 10 }}
          />
          <input
            type="number"
            value={chunkY}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setChunkY(e.target.value)}
            placeholder="Chunk Y"
            style={{ width: 60, fontSize: 10 }}
          />
          <button
            type="button"
            disabled={isBusy}
            onClick={() => {
              const cx = Number.parseInt(chunkX, 10);
              const cy = Number.parseInt(chunkY, 10);
              if (Number.isNaN(cx) || Number.isNaN(cy)) {
                onStateChange({ isBusy: false, lastMessage: 'Enter valid chunk coordinates' });
                return;
              }
              void callAdminEndpoint('/api/admin/dev/generate-chunk', 'Generate chunk', {
                chunkX: cx,
                chunkY: cy,
              });
            }}
            style={{
              padding: '2px 6px',
              fontSize: 10,
              backgroundColor: '#2d3748',
              color: '#edf2f7',
              border: '1px solid #4a5568',
              borderRadius: 0,
              cursor: isBusy ? 'default' : 'pointer',
            }}
          >
            Generate
          </button>
         </div>
       </div>
     </div>
   );
};
